import { useMemo } from 'react';
import Button from './Buttons';

import Table from '../Components/Tables';
import { formatDateUTC } from '../utils/formatDate';
import ChatAnalysisExpandedRow from './ChatAnalysisExpandedRow';
import DateRangeSelector from './DateRangeSelector';
import './TotalAnalytics.css';

const chatColumns = [
  {
    name: 'Channel Name',
    selector: (row) => row.channel_name,
    sortable: false,
  },
  {
    name: 'Chat Name',
    selector: (row) => row.name,
    sortable: false,
  },
  {
    name: 'Created',
    selector: (row) => formatDateUTC(row.created_at),
    sortable: false,
  },
  {
    name: 'Updated',
    selector: (row) => formatDateUTC(row.updated_at),
    sortable: false,
  },
];

const PostCallAnalyticsSummary = ({
  summary,
  loading,
  numericMetrics = [],
  categoricalMetrics = [],
  chats = [],
  chatsTotal = 0,
  chatsLoading = false,
  chatsLimit = 10,
  onChatsPageChange = (skip, limit) => {},
  onPeriodChange,
  onRefresh,
  exportSlot = null,
}) => {
  const hasTrackableMetrics =
    (numericMetrics?.length || 0) > 0 || (categoricalMetrics?.length || 0) > 0;

  const numericRows = useMemo(() => summary?.numeric || [], [summary]);
  const categoricalRows = useMemo(() => summary?.categorical || [], [summary]);
  const booleanRows = useMemo(() => summary?.boolean || [], [summary]);

  const formatValue = (v) => {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'number') return v.toLocaleString();
    return String(v);
  };

  const getSortedCategoryEntries = (counts) => {
    return Object.entries(counts || {}).sort((a, b) => (b[1] || 0) - (a[1] || 0));
  };

  // === Distribution as column (for tables); trims long lists
  const renderDistributionColumn = (counts, maxItems = 6) => {
    const entries = getSortedCategoryEntries(counts);

    if (entries.length === 0) {
      return <span>—</span>;
    }

    const visible = entries.slice(0, maxItems);
    const rest = entries.length - maxItems;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {visible.map(([k, v]) => (
          <div key={k}>
            {k}: {formatValue(v)}
          </div>
        ))}
        {rest > 0 && (
          <div className="small-font" style={{ opacity: 0.8 }}>
            +{rest} more
          </div>
        )}
      </div>
    );
  };

  const renderChatsCell = (value) => {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '2px',
        }}
      >
        <div className="small-font" style={{ opacity: 0.8 }}>
          Total
        </div>
        <div>{(value || 0).toLocaleString()}</div>
      </div>
    );
  };

  // === Small table cosmetics: separators + better column spacing (no design change)
  const rowSeparatorColor = 'rgba(0,0,0,0.06)';
  const cellPaddingY = '14px';

  const chatsColStyle = {
    textAlign: 'right',
    verticalAlign: 'top',
    paddingRight: '22px',
    paddingTop: cellPaddingY,
    paddingBottom: cellPaddingY,
    width: '160px',
  };

  const distributionColStyle = {
    verticalAlign: 'top',
    paddingLeft: '22px',
    paddingTop: cellPaddingY,
    paddingBottom: cellPaddingY,
    borderLeft: `1px solid ${rowSeparatorColor}`,
  };

  const metricColStyle = {
    verticalAlign: 'top',
    paddingTop: cellPaddingY,
    paddingBottom: cellPaddingY,
  };

  return (
    <div className="radius-yes p-2 bg-yes col-12">
      <div className="d-flex flex-row justify-between align-center">
        <h2 className="mb-4">Post-Call Analytics</h2>

        <Button variant="secondary" size="medium" onClick={onRefresh} disabled={loading}>
          <i className="fa-solid fa-rotate-right"></i> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="small-font">Loading post-call analytics…</div>
      ) : !hasTrackableMetrics ? (
        <div className="small-font">
          No analytics summary available. Add at least one <b>numeric</b> or <b>categorical</b>{' '}
          metric.
        </div>
      ) : !summary ? (
        <div className="small-font">No summary data (stub/backend not ready).</div>
      ) : (
        <>
          {/* Numeric table */}
          {numericRows.length > 0 && (
            <div className="mt-2">
              <h3 className="h3-title">Numeric metrics</h3>
              <div className="small-font mb-1">
                For each numeric metric: chats count, min, max, average.
              </div>

              <div className="border-yes radius-yes p-2">
                <table className="table w-100">
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Metric</th>
                      <th style={{ textAlign: 'right' }}>Chats</th>
                      <th style={{ textAlign: 'right' }}>Min</th>
                      <th style={{ textAlign: 'right' }}>Max</th>
                      <th style={{ textAlign: 'right' }}>Avg</th>
                    </tr>
                  </thead>
                  <tbody>
                    {numericRows.map((m) => (
                      <tr key={m.metric_key}>
                        <td>{m.display_name}</td>
                        <td style={{ textAlign: 'right' }}>
                          {(m.chats_with_value || 0).toLocaleString()}
                        </td>
                        <td style={{ textAlign: 'right' }}>{formatValue(m.min)}</td>
                        <td style={{ textAlign: 'right' }}>{formatValue(m.max)}</td>
                        <td style={{ textAlign: 'right' }}>{formatValue(m.avg)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Categorical table */}
          {categoricalRows.length > 0 && (
            <div className="mt-2">
              <h3 className="h3-title">Categorical metrics</h3>
              <div className="small-font mb-1">
                For each categorical metric: number of chats for each category.
              </div>

              <div className="border-yes radius-yes p-2">
                <table className="table w-100" style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Metric</th>
                      <th style={{ textAlign: 'right', width: '160px', paddingRight: '22px' }}>
                        Chats
                      </th>
                      <th
                        style={{
                          textAlign: 'left',
                          paddingLeft: '22px',
                          borderLeft: `1px solid ${rowSeparatorColor}`,
                        }}
                      >
                        Chats Distribution
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoricalRows.map((m, idx) => (
                      <tr
                        key={m.metric_key}
                        style={{
                          borderBottom:
                            idx === categoricalRows.length - 1
                              ? 'none'
                              : `1px solid ${rowSeparatorColor}`,
                        }}
                      >
                        <td style={metricColStyle}>{m.display_name}</td>
                        <td style={chatsColStyle}>{renderChatsCell(m.chats_with_value)}</td>
                        <td style={distributionColStyle}>
                          {renderDistributionColumn(m.counts, 6)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {booleanRows.length > 0 && (
        <div className="mt-2">
          <h3 className="h3-title">Boolean metrics</h3>
          <div className="small-font mb-1">
            For each boolean metric: chats count, true count, false count.
          </div>

          <div className="border-yes radius-yes p-2">
            <table className="table w-100">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Metric</th>
                  <th style={{ textAlign: 'right' }}>Chats</th>
                  <th style={{ textAlign: 'right' }}>True</th>
                  <th style={{ textAlign: 'right' }}>False</th>
                </tr>
              </thead>
              <tbody>
                {booleanRows.map((m) => (
                  <tr key={m.metric_key}>
                    <td>{m.display_name}</td>
                    <td style={{ textAlign: 'right' }}>
                      {(m.chats_with_value || 0).toLocaleString()}
                    </td>
                    <td style={{ textAlign: 'right' }}>{formatValue(m.true_count)}</td>
                    <td style={{ textAlign: 'right' }}>{formatValue(m.false_count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="input-block d-flex flex-column justify-between">
        <h2 className="mb-4">Chat Analytics</h2>
        <Table
          title=""
          columns={chatColumns}
          data={chats}
          progressPending={chatsLoading}
          expandableRows={true}
          selectableRowsHighlight
          expandableRowsComponent={ChatAnalysisExpandedRow}
          paginationPerPage={chatsLimit}
          paginationTotalRows={chatsTotal}
          onChangePage={onChatsPageChange}
        />
      </div>

      {/* period selector */}
      <div style={{ marginTop: '6vh' }}>
        <DateRangeSelector storageKey="post-call-chats" onChange={onPeriodChange} />
      </div>
      <div style={{ marginTop: '3vh' }}>{exportSlot}</div>
    </div>
  );
};

export default PostCallAnalyticsSummary;
